import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './AR7CheatSheet.css';

function PriceCapExplainer() {
  useEffect(() => {
    document.title = 'How the energy price cap works | electricitybills.uk';

    const ogTags = {
      'og:title': 'How the energy price cap works',
      'og:description': 'A visual explainer of how Ofgem sets the UK energy price cap — hedging methodology, observation windows, and backwardation.',
      'og:image': 'https://electricitybills.uk/price_cap_imgs/shaping.png',
      'og:url': 'https://electricitybills.uk/price-cap',
      'twitter:card': 'summary_large_image',
      'twitter:image': 'https://electricitybills.uk/price_cap_imgs/shaping.png',
    };

    const elements = [];
    for (const [key, value] of Object.entries(ogTags)) {
      const attr = key.startsWith('twitter:') ? 'name' : 'property';
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        elements.push(el);
      }
      el.setAttribute('content', value);
    }

    return () => elements.forEach(el => el.remove());
  }, []);

  return (
    <div className="ar7-container">
      <div className="ar7-content">
        <h1>How the energy price cap works</h1>

        <p>
          The UK energy price cap is unveiled by Ofgem every 3 months, and reliably claims the
          top spot on the news. It determines the maximum rate that an energy supplier can charge
          a residential customer for electricity and gas.
        </p>

        <p>The price cap goes up and down for two reasons.</p>
        <ol>
          <li>Energy gets more or less expensive to buy</li>
          <li>Charges and levies are implemented by the government and Ofgem to pay for grid infrastructure
            and "policy costs", which are primarily long-term contracts for renewables, nuclear and gas plants.</li>
        </ol>

        <p>
          You can learn more about levies <Link to="/">here</Link>. The aim of this post is to
          explain how the changing cost of energy affects the price cap.
        </p>

        <h2>Timing</h2>
        <p>
          A new price cap is set every 3 months. It used to be 6 months, but we upped the
          frequency after Ukraine in 2022.
        </p>

        <p>
          The price cap is determined in advance by a 3 month observation window.
        </p>

        <img src="/price_cap_imgs/window.png" alt="Price cap observation window" />

        <p>
          Right now it's March 2026, and if your bills haven't risen since the Iran War, this is
          why. Most households will only see the effect of higher energy prices from July.
        </p>

        <h2>Hedging methodology</h2>
        <p>
          The price cap assumes that suppliers buy their energy using a pre-determined methodology.
        </p>

        <p>
          Suppliers are in fact free to choose how they buy their energy, but if they do something
          different, they expose themselves to the risk of a lower price cap than they can deliver.
        </p>

        <p>The current price cap methodology is called a 3-1.5-12 hedge.</p>

        <img src="/price_cap_imgs/hedging.png" alt="3-1.5-12 hedging methodology" />

        <p>
          This feels a bit strange. Why are we indexing against electricity forwards for a whole
          year, when the price cap window only applies to 3 months? In fact, the original idea of the price cap was to give an annualised figure that doesn't change seasonally - but this becomes tricky during crises. We now have a seasonal tweak called "backwardation" which we'll cover later.
        </p>

        <p>
          
        </p>

        <h2>Price cap inputs</h2>
        <p>
          Note: the only two inputs to the price cap are the cost of <strong>Baseload</strong> and{' '}
          <strong>Peak</strong> futures. These are assumed to be bought in the ratio of 70% / 30% all year round.
        </p>

        <img src="/price_cap_imgs/shaping.png" alt="Baseload and Peak shaping" className="img-85" />

        <p>
          Once the supplier has bought Baseload and Peak, they are left with a very blocky profile.
          They must then <strong>shape</strong> this profile by conducting additional trades and using power
          exchanges to trade half-hourly volumes at Day-Ahead and Intraday.
        </p>

        <p>
          Notably, exchange-traded prices do not feed into the price cap. Instead, a fixed
          allowance of 7.38% is given for shaping. This is applied as a fixed multiplier on
          the 70% Baseload, 30% Peak weighted average.
        </p>

        <h2>Predicting the price cap</h2>
        <p>
          If you note down the price of Baseload and Peak electricity futures every day, then at
          the end of the observation window you'll know exactly what the price cap is.
        </p>

        <p>
          I track the current observation window every day
          at <Link to="/wholesale">electricitybills.uk/wholesale</Link>.
        </p>

        <img src="/price_cap_imgs/price_cap_screenshot.png" alt="Price cap observation window tracker" />

        <h2>Gas</h2>
        <p>
          The gas price cap works similarly, over a 3 month window. There are two major differences:
        </p>
        <ul>
          <li>
            Gas is traded daily (no peak or half-hourly shaping). Only one quarterly forward
            product is tracked.
          </li>
          <li>
            The demand weights between seasons are much stronger (winter gas use is very high,
            summer is low).
          </li>
        </ul>

        <h2>Backwardation</h2>
        <p>
          There is a snag with indexing against 12 months of delivery when the price cap only
          applies for 3.
        </p>

        <p>
          Consider a winter price cap. The index averages forward prices across the next 12 months.
          But summer quarters are cheaper, so the annual average gets dragged below what it actually
          costs to supply energy that winter. The reverse happens in summer: expensive winter
          quarters inflate the average above the real cost.
        </p>

        <p>
          Over a year these effects cancel out, which is why the old 6-month cap didn't need a
          correction. But with a 3 month price cap the mismatch got worse.
        </p>

        <p>
          Backwardation computes a second index covering only the upcoming quarter (a 3-1.5-3
          hedge). The difference between the quarterly and annual cap is the backwardation cost,
          and it goes both ways. Backwardation gets applied as a correction to the wholesale
          price cap.
        </p>

        <img src="/price_cap_imgs/backwardation.png" alt="Backwardation adjustment" className="img-85" />

        <p className="footer-text">
          Thanks for reading. I don't work for Ofgem, so I may have gotten things wrong.
          Let me know :)
          <br />&mdash; <a href="https://benjames.io" target="_blank" rel="noopener noreferrer">Ben James</a>
        </p>

        <Link to="/" className="back-button">
          &larr; Back to Bill Breakdown
        </Link>
      </div>
    </div>
  );
}

export default PriceCapExplainer;
